import { ColumnDef, createColumnHelper, RowSelectionState } from '@tanstack/react-table';
import clsx from 'clsx';
import {
  ReactElement, Suspense, useCallback, useEffect, useMemo, useState
} from 'react';
import { FaPlay } from 'react-icons/fa';
import { FiMoreVertical } from 'react-icons/fi';
import { MdOutlineAnalytics } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast,ToastContainer } from 'react-toastify';
import { match } from 'ts-pattern';
import 'react-toastify/dist/ReactToastify.css';

import { MetricsTable } from '@/app/evaluations/_metricsTable/metricsTable';
import ModalRunCreate from '@/app/evaluations/_modalRunCreate/modalRunCreate';
import { ScheduleTable } from '@/app/evaluations/_scheduleTable/scheduleTable';
import { Banner } from '@/components/banner/banner';
import { Button } from '@/components/button/button';
import { DetailPageHeader } from '@/components/detailPageHeader/detailPageHeader';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Loader } from '@/components/loader/loader';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { StatusIcon } from '@/components/statusIcon/statusIcon';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Tabs } from '@/components/tabs/tabs';
import {
  useCreateRun, useCreateSchedule, useEvalRuns, useSetDeprecation
} from '@/hooks/queries/useEvaluations';
import { useOrgUsersMapQuery } from '@/hooks/queries/useOrg';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { isTerminalStatus, useRunProgress } from '@/hooks/useRunProgress';
import {
  RunCreateReq, RunMetricDataMap, RunProgressMessage, ScheduleCreateReq, TERMINAL_RUN_STATUSES
} from '@/types/evals';
import { formatScore } from '@/utils/numberUtils';
import { formatRunCreateError, parseRunCreateError } from '@/utils/runCreateError';
import { parseParams } from '@/utils/searchParams';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';

import styles from './page.module.scss';

const columnHelper = createColumnHelper<Record<string, unknown>>();

type SearchParams = {
  eid: string;
  tab?: string;
};

type EvaluationTab = 'runs' | 'schedule' | 'metrics';
const TABS: readonly EvaluationTab[] = [ 'runs', 'schedule', 'metrics' ] as const;
const isTab = ( v: string | undefined ): v is EvaluationTab => !!v && ( TABS as readonly string[] ).includes( v );

/**
 * Streams progress for a single active run.
 * Renders nothing - only manages the SSE connection and relays updates via callbacks.
 */
function RunStreamWatcher( { runId, onProgress, onComplete }: {
  runId: string;
  onProgress: ( runId: string, progress: RunProgressMessage ) => void;
  onComplete: ( runId: string ) => void;
} ) {
  const { progress, isTerminal } = useRunProgress( runId, true );

  useEffect( () => {
    if( progress ) onProgress( runId, progress );
  }, [ progress, runId, onProgress ] );

  useEffect( () => {
    if( isTerminal ) onComplete( runId );
  }, [ isTerminal, runId, onComplete ] );

  return null;
}

function EvaluationRunsPage() {
  const navigate = useNavigate();
  const [ searchParams, setSearchParams ] = useSearchParams();
  const {
    eid: qEvaluationId = '',
    tab: qTab
  } = parseParams<SearchParams>( searchParams );
  const activeTab: EvaluationTab = isTab( qTab ) ? qTab : 'runs';
  const setActiveTab = useCallback( ( next: EvaluationTab ) => {
    setSearchParams( prev => {
      const params = new URLSearchParams( prev );
      if ( next === 'runs' ) params.delete( 'tab' );
      else params.set( 'tab', next );
      return params;
    } );
  }, [ setSearchParams ] );
  const [ showModalRun, setShowModalRun ] = useState<boolean>( false );
  const [ useLocalZone ] = useLocalZonePreference();
  const [ progressMap, setProgressMap ] = useState<Record<string, RunProgressMessage>>( {} );
  const [ rowSelection, setRowSelection ] = useState<RowSelectionState>( {} );

  // React Query hooks
  const { data: evalRunsData, isLoading, refetch: refetchEvalRuns } = useEvalRuns( qEvaluationId );
  const { data: usersMap } = useOrgUsersMapQuery();
  const setDeprecationMutation = useSetDeprecation( qEvaluationId );
  const createRunMutation = useCreateRun( qEvaluationId );
  const createScheduleMutation = useCreateSchedule( qEvaluationId );

  const evaluation = evalRunsData?.evaluation;
  const runs = evalRunsData?.runs || [];

  // Find runs that need streaming (non-terminal status)
  const activeRunIds = useMemo( () => runs
    .filter( r => !( TERMINAL_RUN_STATUSES as readonly string[] ).includes( r.run.status ) )
    .map( r => r.run.id ),
  [ runs ] );

  const handleStreamProgress = useCallback( ( runId: string, progress: RunProgressMessage ) => {
    setProgressMap( prev => ( { ...prev, [ runId ]: progress } ) );
  }, [] );

  const handleStreamComplete = useCallback( ( runId: string ) => {
    setProgressMap( prev => {
      const next = { ...prev };
      delete next[ runId ];
      return next;
    } );
    refetchEvalRuns();
  }, [ refetchEvalRuns ] );

  // Drop progress entries for runs that are no longer active. Guards against the
  // race where a refetch flips run B to terminal before its SSE stream delivers
  // a terminal event, leaving a stale percentage stranded in progressMap.
  useEffect( () => {
    setProgressMap( prev => {
      const active = new Set( activeRunIds );
      let changed = false;
      const next: Record<string, RunProgressMessage> = {};
      for( const [ id, p ] of Object.entries( prev ) ) {
        if( active.has( id ) ) next[ id ] = p;
        else changed = true;
      }
      return changed ? next : prev;
    } );
  }, [ activeRunIds ] );

  // Derive metric data from runs
  const [ runMetricDataMap, metricKeys ] = useMemo( () => {
    const data: RunMetricDataMap = {};
    const metricKeySet: Set<string> = new Set();

    if ( runs.length === 0 ) return [ data, [] as string[] ];

    for ( const run of runs ) {
      if ( !run.details ) continue;

      for ( const metric of run.details.metrics ) {
        const combinedIndex = `${ metric.metricProviderId }|${ metric.metricName }`;
        metricKeySet.add( combinedIndex );

        data[ run.run.id ] = {
          ...data[ run.run.id ],
          [ combinedIndex ]: metric
        };
      }
    }

    const sortedUniqueMetricKeys = Array.from( metricKeySet ).sort();
    return [ data, sortedUniqueMetricKeys ];
  }, [ runs ] );

  /* HELPERS */
  const getUserDisplayName = ( userId?: string ): string => {
    if ( !userId ) return '-';

    const user = usersMap?.get( userId );
    return user?.name || user?.email || userId;
  };

  // Memoize table columns
  const tableColumns = useMemo<ColumnDef<Record<string, unknown>>[]>( () => {
    return [
      columnHelper.display( {
        id: 'select',
        size: 44,
        header: ( { table } ) => (
          <input
            type="checkbox"
            aria-label="Select all runs"
            checked={ table.getIsAllPageRowsSelected() }
            onChange={ table.getToggleAllPageRowsSelectedHandler() }
            onClick={ ( e ) => e.stopPropagation() }
          />
        ),
        cell: ( { row } ) => (
          <input
            type="checkbox"
            aria-label="Select run"
            checked={ row.getIsSelected() }
            onChange={ row.getToggleSelectedHandler() }
            onClick={ ( e ) => e.stopPropagation() }
          />
        ),
        meta: {
          classStr: buildColumnClassString( { width: 44, styles: ColumnModEnums.CENTER } ),
          fixedWidth: '44px'
        }
      } ),
      columnHelper.accessor( 'runId', {
        id: 'runId',
        header: 'Run ID',

        size: 300,
        cell: info => info.getValue(),
        meta: {
          classStr: buildColumnClassString( { width: 300, styles: ColumnModEnums.NO_OVERFLOW } ),
          fixedWidth: '300px'
        }
      } ),
      columnHelper.accessor( 'ranAt', {
        id: 'ranAt',
        header: 'Ran At',
        size: 250,
        cell: info => info.getValue(),
        meta: {
          classStr: buildColumnClassString( { width: 250, styles: ColumnModEnums.NO_OVERFLOW } ),
          fixedWidth: '250px'
        }
      } ),
      columnHelper.accessor( 'conversations', {
        id: 'conversations',
        header: '# Conversations',
        size: 160,
        cell: info => info.getValue(),
        meta: {
          classStr: buildColumnClassString( { width: 160, styles: ColumnModEnums.CENTER } ),
          fixedWidth: '160px'
        }
      } ),
      ...metricKeys.map( metricKey => {
        const [ _, metricName ] = metricKey.split( '|' );

        return columnHelper.accessor( metricKey, {
          id: metricKey,
          header: metricName,
          size: 190,
          cell: info => info.getValue(),
          meta: {
            classStr: buildColumnClassString( { width: 190, styles: [ ColumnModEnums.CENTER, ColumnModEnums.NO_OVERFLOW ] } ),
            fixedWidth: '190px'
          }
        } );
      } )
    ];
  }, [ metricKeys ] );

  // Memoize table data
  const tableData = useMemo<Record<string, unknown>[]>( () => {
    const data: Record<string, unknown>[] = [];

    for( const [ i, { run } ] of runs.entries() ) {
      // Belt-and-suspenders: even if a stale entry slips into progressMap, never
      // show a percentage on a row whose run is already terminal.
      const progressData = !isTerminalStatus( run.status ) ? progressMap[ run.id ] : undefined;

      const row: Record<string, unknown> = {
        _rid: run.id,
        runId: (
          <div className={ styles.runIdCell }>
            <StatusIcon key={ i + '-status' } status={ run.status } />
            <span className={ styles.runIdText }>{ run.id }</span>
            { progressData && (
              <span className={ styles.progress_text }>{ progressData.syncPercentComplete }%</span>
            ) }
          </div>
        ),
        ranBy: getUserDisplayName( run.createdBy ),
        ranAt: (
          <TimeFormatter
            key={ run.id }
            sourceType={ TimeSourceTypeEnum.ISO }
            value={ run.createdAt }
            outputFormat={ OutputFormatEnum.STRING_SINGLE }
            opts={ { useLocalZone } }
          />
        ),
        conversations: run.totalExamples || '-'
      };

      const runMetrics = runMetricDataMap[ run.id ];

      for( const metricKey of metricKeys ) {
        row[ metricKey ] = formatScore( runMetrics?.[ metricKey ]?.averageScore );
      }

      data.push( row );
    }

    return data;
  }, [ runs, runMetricDataMap, metricKeys, evaluation?.id, useLocalZone, progressMap ] );

  // Create table instance with column pinning
  const runsTable = useNativeTable( {
    data: tableData,
    columns: tableColumns,
    getRowId: ( row ) => row._rid as string,
    enableRowSelection: true,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    initialState: {
      columnPinning: {
        left: [ 'select', 'runId', 'ranAt', 'conversations' ]
      },
      pagination: {
        pageSize: 30
      }
    }
  } );

  const selectedRunIds = useMemo( () => Object.keys( rowSelection ).filter( id => rowSelection[ id ] ), [ rowSelection ] );

  const handleCompareRuns = useCallback( () => {
    if ( !evaluation?.id || selectedRunIds.length < 2 ) return;
    const params = new URLSearchParams( { eid: evaluation.id, rids: selectedRunIds.join( ',' ) } );
    navigate( `/evaluations/compare?${ params.toString() }` );
  }, [ evaluation?.id, selectedRunIds, navigate ] );

  const handleRowClick = useCallback( ( row: Record<string, unknown> ) => {
    const rid = row._rid;
    if ( !evaluation?.id || typeof rid !== 'string' ) return;
    const params = new URLSearchParams( { eid: evaluation.id, rid } );
    navigate( `/evaluations/results?${ params.toString() }` );
  }, [ evaluation?.id, navigate ] );

  const _setDeprecation = ( isDeprecated: boolean ) => {
    if( !evaluation ) return;

    setDeprecationMutation.mutate( { isDeprecated }, {
      onSuccess: () => {
        toast.success( `Evaluation ${ isDeprecated ? 'deprecated' : 'undeprecated' }` );
        refetchEvalRuns();
      },
      onError: ( error ) => {
        const errorMessage = error instanceof Error ? error.message : 'Unable to update deprecation status';
        toast.error( errorMessage );
      }
    } );
  };


  /* HANDLERS */
  // Success and error are both rendered inside the modal — success via the
  // modal's `isRunSuccess`/`isScheduleSuccess` success view, error via the
  // inline banner driven by `runErrorMessage`/`scheduleErrorMessage`. The
  // parent only handles the missing-evaluation guard and (via
  // `handleModalClose`) the user-initiated close.
  const onRunCreate = useCallback( ( ret: RunCreateReq ) => {
    if( !evaluation ) {
      console.error( 'no evaluation context, unable to create run' );
      toast.error( 'no evaluation context, unable to create run' );
      return;
    }

    createRunMutation.mutate( ret, {
      onError: ( error ) => {
        console.error( error );
      }
    } );
  }, [ evaluation, createRunMutation ] );

  const onScheduleCreate = useCallback( ( ret: ScheduleCreateReq ) => {
    if( !evaluation ) {
      console.error( 'no evaluation context, unable to create schedule' );
      toast.error( 'no evaluation context, unable to create schedule' );
      return;
    }

    createScheduleMutation.mutate( { ...ret, evaluation_id: evaluation.id }, {
      onError: ( error ) => {
        console.error( error );
      }
    } );
  }, [ evaluation, createScheduleMutation ] );

  const handleModalClose = useCallback( () => {
    if ( createRunMutation.isPending || createScheduleMutation.isPending ) return;
    setShowModalRun( false );
    createRunMutation.reset();
    createScheduleMutation.reset();
  }, [ createRunMutation, createScheduleMutation ] );

  const runErrorMessage = createRunMutation.isError
    ? formatRunCreateError( parseRunCreateError( createRunMutation.error ).detail )
    : undefined;
  const scheduleErrorMessage = createScheduleMutation.isError
    ? formatRunCreateError( parseRunCreateError( createScheduleMutation.error ).detail )
    : undefined;
  /* RENDERS */
  const renderHeaderActions = (): ReactElement => (
    <div className={ styles.headerActions }>
      <Button
        variant='primary'
        className={ styles.createRunButton }
        icon={ <Icon icon={ FaPlay } size="xs" /> }
        onClick={ () => setShowModalRun( !showModalRun ) }
        disabled={ evaluation?.isDeprecated }
        title={ evaluation?.isDeprecated ? 'Un-deprecate to create a run' : undefined }
      >Create Run
      </Button>
      {/* TODO(KANP-1410): per-eval role is not on the Evaluation payload yet, so
          owner-only actions are shown unconditionally. Gate on real role later. */}
      <Menu
        variant="menu"
        icon={ <Icon icon={ FiMoreVertical } /> }
        data-testid="eval-actions-menu"
      >
        <MenuItem
          value={ evaluation?.isDeprecated ? 'Un-deprecate Evaluation' : 'Deprecate Evaluation' }
          onClick={ () => _setDeprecation( !evaluation?.isDeprecated ) }
          disabled={ setDeprecationMutation.isPending }
          data-testid="eval-action-deprecate"
        />
      </Menu>
    </div>
  );

  const renderStreamingIndicator = (): ReactElement | null => {
    if( activeRunIds.length === 0 ) return null;

    return (
      <div className={ clsx( 'layout__row', 'pad', styles.layout__actions ) }>
        <div className={ styles.streaming_indicator }>
          <span className={ styles.streaming_dot } />
          <span>Live — streaming { activeRunIds.length } active run{ activeRunIds.length > 1 ? 's' : '' }</span>
        </div>
      </div>
    );
  };

  const render = (): ReactElement => (
    <>
      { activeRunIds.map( id => (
        <RunStreamWatcher
          key={ id }
          runId={ id }
          onProgress={ handleStreamProgress }
          onComplete={ handleStreamComplete }
        />
      ) ) }
      {
        showModalRun && evaluation && (
          <ModalRunCreate
            evaluationId={ evaluation.id }
            evaluationName={ evaluation.name }
            onCreateRun={ onRunCreate }
            onCreateSchedule={ onScheduleCreate }
            onClose={ handleModalClose }
            isRunPending={ createRunMutation.isPending }
            isRunSuccess={ createRunMutation.isSuccess }
            isRunError={ createRunMutation.isError }
            runErrorMessage={ runErrorMessage }
            isSchedulePending={ createScheduleMutation.isPending }
            isScheduleSuccess={ createScheduleMutation.isSuccess }
            isScheduleError={ createScheduleMutation.isError }
            scheduleErrorMessage={ scheduleErrorMessage }
          />
        )
      }

      <DetailPageHeader
        backTo="/evaluations"
        backLabel="Back to Evaluations"
        icon={ <Icon icon={ MdOutlineAnalytics } /> }
        title={ evaluation?.name }
        subtitle="Evaluation"
        description={ evaluation?.description ? `Description: ${ evaluation.description }` : undefined }
        actions={ renderHeaderActions() }
      />

      <Tabs<EvaluationTab>
        items={ [
          { value: 'runs', label: 'Runs', 'data-testid': 'tab-runs' },
          { value: 'schedule', label: 'Evaluation Schedule', 'data-testid': 'tab-schedule' },
          { value: 'metrics', label: 'Metrics', 'data-testid': 'tab-metrics' }
        ] }
        active={ activeTab }
        onChange={ setActiveTab }
        variant="plain"
        data-testid="evaluation-tabs"
      />

      { evaluation?.isDeprecated && (
        <Banner variant="warning" className={ styles.deprecatedBanner } data-testid="eval-deprecated-banner">
          <strong>This evaluation has been deprecated.</strong>
          <br />
          To create a run, first un-deprecate this evaluation using the action menu.
        </Banner>
      ) }

      { match( activeTab )
        .with( 'runs', () => (
          <>
            <div className={ clsx( 'layout__row', styles.toolbar ) }>
              <div className={ styles.toolbarLeft }>
                <span className={ styles.compareHint }>Select runs to compare</span>
                <Button
                  variant="secondary"
                  onClick={ handleCompareRuns }
                  disabled={ selectedRunIds.length < 2 }
                  data-testid="compare-runs"
                >Compare Runs
                </Button>
              </div>
            </div>
            { renderStreamingIndicator() }
            <div className={ clsx( styles.layout__runs ) }>
              <NativeTable table={ runsTable } onRowClick={ handleRowClick } />
            </div>
          </>
        ) )
        .with( 'schedule', () => (
          evaluation ? <ScheduleTable evaluationId={ evaluation.id } /> : null
        ) )
        .with( 'metrics', () => (
          evaluation ? <MetricsTable evaluation={ evaluation } /> : null
        ) )
        .exhaustive() }
    </>
  );

  return (
    <div className={ clsx( 'page' ) }>
      <ToastContainer />
      {
        isLoading
          ? <Loader />
          : render()
      }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <EvaluationRunsPage />
    </Suspense>
  );
}
